/**
 * Lists.
 *
 * Built against taskdojo-app's ListsScreen and ListDetailScreen: a grid of
 * cards, each showing the list's icon, name, item count and how far through it
 * you are, and a detail view for its items.
 *
 * The part that is not styling is sharing, and it is the reason this screen
 * needed more than a repaint. A list has an `owner` and a `members` array, and
 * firestore.rules draws the line between them:
 *
 *     read   -- uid in members
 *     update -- uid in members        (so any member may edit, and leave)
 *     delete -- owner == uid          (only the owner)
 *     items  -- read/write if uid in the parent list's members
 *
 * The previous version of this screen never read `owner` at all -- it wrote it
 * on create and never checked it again -- so it offered Delete to every member.
 * That is worse than a control that fails, because the delete ran in two parts:
 * the item loop succeeded (members may write items) and the list document then
 * failed on the rules. A member pressing Delete emptied somebody else's shared
 * list and left the list itself behind. Deleting is now the owner's only, and
 * everyone else gets Leave, which is the one edit the rules actually allow them
 * -- taking themselves out of `members`.
 *
 * Lists are queried by membership, never by ownership. Firestore rejects a
 * query whose filter does not guarantee every match is readable, so
 * where('owner','==',uid) is refused outright, before it reads anything.
 *
 * Items carry `createdBy`, and ticking one writes `checkedBy` and `updatedBy`.
 * These are not decoration. functions/index.js notifies the other members of a
 * shared list on both events and bails out early if the field is missing --
 * `if (!creatorId) return null` and `if (!actorId) return null` -- so an item
 * added or ticked without them notifies nobody, silently, while the same action
 * on the phone does. Writing them is what keeps the two platforms behaving the
 * same from the other members' point of view.
 *
 * Counts are maintained with increment() rather than read-then-write. Two
 * people ticking items on a shared list at the same time would otherwise each
 * write a total computed from a stale read, and the count drifts away from the
 * items.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection, query, where, onSnapshot, doc, setDoc, updateDoc, deleteDoc,
  getDocs, serverTimestamp, increment, arrayRemove, arrayUnion,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useApp } from '../../contexts/AppContext';
import { useConnections } from '../../contexts/ConnectionsContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ScreenHeader, SummaryBand, ActionButton, PrimaryButton, SecondaryButton,
  Field, useInputClass, raised, pressable,
} from '../dojo';
import { LIST_COLORS, resolveListColors, DEFAULT_LIST_COLORS } from '../../constants/listColors';
import { readableOn, MARK_MIN } from '../../utils/taskColor';
import { EMOJI_OPTIONS, resolveIcon } from '../../constants/listIcons';
import { generateId } from '../../services/firebaseService';

interface ListItem {
  id: string;
  text: string;
  checked: boolean;
  createdAt: any;
  updatedAt: any;
}

interface ListData {
  id: string;
  name: string;
  owner: string;
  ownerEmail?: string;
  members: string[];
  color: [string, string] | string;
  icon: string;
  itemCount: number;
  checkedCount: number;
  createdAt: any;
  updatedAt: any;
}

const Lists: React.FC = () => {
  const { state } = useApp();
  const { connections } = useConnections();
  const { colors, isDark } = useTheme();
  /** The surface marks and chips sit on, which is what contrast is measured against. */
  const card = isDark ? '#1B2A32' : '#FFFFFF';

  const input = useInputClass();

  const uid = state.user?.uid;

  const [lists, setLists] = useState<ListData[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [items, setItems] = useState<ListItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [busyItem, setBusyItem] = useState<string | null>(null);

  const [editing, setEditing] = useState<ListData | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState(EMOJI_OPTIONS[0]);
  const [color, setColor] = useState<[string, string]>(DEFAULT_LIST_COLORS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState<{ list: ListData; mode: 'delete' | 'leave' } | null>(null);
  const [membersOpen, setMembersOpen] = useState(false);

  /* ------------------------------- the lists ------------------------------- */

  useEffect(() => {
    if (!uid) return;
    // By membership, not ownership -- see the note at the top of the file.
    const q = query(collection(db, 'lists'), where('members', 'array-contains', uid));
    const unsub = onSnapshot(q, (snap) => {
      const out: ListData[] = [];
      snap.forEach((d) => out.push({ ...(d.data() as ListData), id: d.id }));
      out.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setLists(out);
      setLoading(false);
    }, (e) => {
      setError(e?.message || "Couldn't load your lists.");
      setLoading(false);
    });
    return unsub;
  }, [uid]);

  /* ------------------------------- its items ------------------------------- */

  useEffect(() => {
    if (!openId) { setItems([]); return; }
    setItemsLoading(true);

    // Deliberately unordered, and sorted here instead.
    //
    // An orderBy('createdAt') query silently drops any document missing the
    // field, and older items predate it -- so the ordered version hides items
    // that exist. Sorting client-side costs nothing at this size and cannot
    // lose a row. Anything without a timestamp sorts to the end rather than
    // disappearing.
    const unsub = onSnapshot(
      collection(db, 'lists', openId, 'items'),
      (snap) => {
        const out: ListItem[] = [];
        snap.forEach((d) => out.push({ ...(d.data() as ListItem), id: d.id }));
        out.sort((a, b) => {
          const at = a.createdAt?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;
          const bt = b.createdAt?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;
          return at - bt;
        });
        setItems(out);
        setItemsLoading(false);
      },
      (e) => {
        // Must clear the flag, or a failure leaves the view saying "Loading…"
        // for ever with no way to tell that anything went wrong.
        setItemsLoading(false);
        setError(e?.message || "Couldn't load this list's items.");
      },
    );
    return unsub;
  }, [openId]);

  const openList = useMemo(() => lists.find((l) => l.id === openId) || null, [lists, openId]);

  const isOwner = useCallback((l: ListData) => !!uid && l.owner === uid, [uid]);

  const memberNames = useCallback((l: ListData) =>
    (l.members || []).map((m) => {
      if (m === uid) return 'You';
      const c = connections.find((x: any) => x.uid === m);
      return c?.displayName || c?.email?.split('@')[0] || 'Someone';
    }), [connections, uid]);

  const totals = useMemo(() => ({
    lists: lists.length,
    items: lists.reduce((s, l) => s + (l.itemCount || 0), 0),
    shared: lists.filter((l) => (l.members || []).length > 1).length,
  }), [lists]);

  /* -------------------------------- writes -------------------------------- */

  const openCreate = () => {
    setName(''); setIcon(EMOJI_OPTIONS[0]); setColor(DEFAULT_LIST_COLORS);
    setEditing(null); setCreating(true); setError('');
  };

  const openEdit = (l: ListData) => {
    setName(l.name || '');
    setIcon(resolveIcon(l.icon));
    setColor(resolveListColors(l.color));
    setEditing(l); setCreating(false); setError('');
  };

  const closeModal = () => { setEditing(null); setCreating(false); setError(''); };

  const saveList = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Give the list a name.'); return; }
    if (!uid) return;

    setSaving(true); setError('');
    try {
      if (editing) {
        await updateDoc(doc(db, 'lists', editing.id), {
          name: name.trim(), icon, color, updatedAt: serverTimestamp(),
        });
      } else {
        const id = generateId('list', uid);
        await setDoc(doc(db, 'lists', id), {
          id, name: name.trim(), owner: uid, ownerEmail: state.user?.email || '',
          members: [uid], color, icon, itemCount: 0, checkedCount: 0,
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
        setOpenId(id);
      }
      closeModal();
    } catch (err: any) {
      setError(err?.message || "Couldn't save that. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /** Owner only. Items go first, because the rules let members write items but
   *  only the owner remove the list, and a half-deleted list is worse than none. */
  const removeList = async (l: ListData) => {
    setSaving(true);
    try {
      const snap = await getDocs(collection(db, 'lists', l.id, 'items'));
      await Promise.all(snap.docs.map((d) => deleteDoc(doc(db, 'lists', l.id, 'items', d.id))));
      await deleteDoc(doc(db, 'lists', l.id));
      if (openId === l.id) setOpenId(null);
      setConfirm(null);
    } catch (err: any) {
      setError(err?.message || "Couldn't delete that list.");
      setConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  /** Everyone else. Removing yourself from members is the one edit the rules
   *  allow a non-owner, and it leaves the list intact for the others. */
  const leaveList = async (l: ListData) => {
    if (!uid) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'lists', l.id), {
        members: arrayRemove(uid),
        updatedAt: serverTimestamp(),
      });
      if (openId === l.id) setOpenId(null);
      setConfirm(null);
    } catch (err: any) {
      setError(err?.message || "Couldn't leave that list.");
      setConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  const addMember = async (l: ListData, friendId: string) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'lists', l.id), {
        members: arrayUnion(friendId),
        updatedAt: serverTimestamp(),
      });
    } catch (err: any) {
      setError(err?.message || "Couldn't add them to this list.");
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (l: ListData, memberId: string) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'lists', l.id), {
        members: arrayRemove(memberId),
        updatedAt: serverTimestamp(),
      });
    } catch (err: any) {
      setError(err?.message || "Couldn't remove them from this list.");
    } finally {
      setSaving(false);
    }
  };

  const addItem = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = newItem.trim();
    if (!text || !openId || !uid) return;
    setNewItem('');
    try {
      const id = generateId('item', uid);
      await setDoc(doc(db, 'lists', openId, 'items', id), {
        id, text, checked: false, createdBy: uid,
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'lists', openId), {
        itemCount: increment(1), updatedAt: serverTimestamp(),
      });
    } catch (err: any) {
      setError(err?.message || "Couldn't add that.");
      setNewItem(text);
    }
  };

  const toggleItem = async (item: ListItem) => {
    if (!openId || !uid || busyItem) return;
    setBusyItem(item.id);
    try {
      const next = !item.checked;
      await updateDoc(doc(db, 'lists', openId, 'items', item.id), {
        checked: next,
        checkedBy: next ? uid : null,
        updatedBy: uid,
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'lists', openId), {
        checkedCount: increment(next ? 1 : -1), updatedAt: serverTimestamp(),
      });
    } catch (err: any) {
      setError(err?.message || "Couldn't save that.");
    } finally {
      setBusyItem(null);
    }
  };

  const removeItem = async (item: ListItem) => {
    if (!openId) return;
    try {
      await deleteDoc(doc(db, 'lists', openId, 'items', item.id));
      await updateDoc(doc(db, 'lists', openId), {
        itemCount: increment(-1),
        checkedCount: increment(item.checked ? -1 : 0),
        updatedAt: serverTimestamp(),
      });
    } catch (err: any) {
      setError(err?.message || "Couldn't delete that.");
    }
  };

  /* -------------------------------- render -------------------------------- */

  const banner = error && (
    <div role="alert" className="mx-5 mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
      px-4 py-3 text-[13px] font-semibold text-dojo-red flex items-start gap-3">
      <span className="flex-1">{error}</span>
      <button type="button" onClick={() => setError('')} aria-label="Dismiss" className="font-black">×</button>
    </div>
  );

  /* ------------------------------ detail view ------------------------------ */
  if (openList) {
    const pair = resolveListColors(openList.color);
    const done = items.filter((i) => i.checked).length;
    const pct = items.length > 0 ? Math.round((done / items.length) * 100) : 0;
    const owner = isOwner(openList);
    const names = memberNames(openList);

    return (
      <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
        <div className="max-w-3xl mx-auto pt-4 pb-24">
          <div className="flex items-center gap-3 px-5 pt-2 pb-4">
            <button
              type="button"
              onClick={() => setOpenId(null)}
              aria-label="Back to lists"
              className={`w-10 h-10 rounded-[12px] flex items-center justify-center flex-shrink-0
                ${colors.textSecondary} ${isDark ? 'hover:bg-dojo-surfaceDeep' : 'hover:bg-stone-100'}`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className="text-[26px] leading-none flex-shrink-0">{resolveIcon(openList.icon)}</span>
            <div className="flex-1 min-w-0">
              <h1 className="text-[26px] font-black tracking-[-0.7px] truncate">{openList.name}</h1>
              <p className={`text-[12.5px] font-bold ${colors.textSecondary}`}>
                {items.length} item{items.length !== 1 ? 's' : ''}
                {openList.members?.length > 1 && ` · shared with ${names.filter((n) => n !== 'You').join(', ')}`}
              </p>
            </div>
            {owner && (
              <button
                type="button"
                onClick={() => setMembersOpen(true)}
                aria-label="Manage members"
                className={`h-10 px-3 rounded-[12px] flex items-center gap-1.5 flex-shrink-0
                  ${raised} ${pressable} ${colors.cardBorder} ${colors.card}
                  ${colors.textSecondary} text-[13px] font-extrabold`}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeLinecap="round" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" strokeLinecap="round" />
                </svg>
                {openList.members?.length || 1}
              </button>
            )}
            <button
              type="button"
              onClick={() => openEdit(openList)}
              aria-label="Edit list"
              className={`w-10 h-10 rounded-[12px] flex items-center justify-center flex-shrink-0
                ${colors.textSecondary} ${isDark ? 'hover:bg-dojo-surfaceDeep' : 'hover:bg-stone-100'}`}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                  strokeLinecap="round" strokeLinejoin="round" />
                <path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          {banner}

          {items.length > 0 && (
            <div className="px-5 mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-[12px] font-bold uppercase tracking-[0.5px] ${colors.textSecondary}`}>
                  {done} of {items.length} done
                </span>
                <span className="text-[13px] font-extrabold tabular-nums">{pct}%</span>
              </div>
              <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-200'}`}>
                <div className="h-full rounded-full transition-[width] duration-500"
                  style={{ width: `${pct}%`, backgroundColor: pair[0] }} />
              </div>
            </div>
          )}

          <form onSubmit={addItem} className="px-5 mb-4 flex gap-2">
            <input
              className={`${input} flex-1`}
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder="Add an item"
            />
            <PrimaryButton type="submit" disabled={!newItem.trim()}>Add</PrimaryButton>
          </form>

          <div className="px-5">
            {itemsLoading ? (
              <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>Loading…</p>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center text-center py-16">
                <span className="text-[40px] mb-3">{resolveIcon(openList.icon)}</span>
                <h2 className="text-lg font-black tracking-[-0.3px] mb-1">Nothing on this list</h2>
                <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>
                  Add the first item above.
                </p>
              </div>
            ) : (
              <ul className="space-y-2 list-none p-0 m-0">
                {items.map((item) => (
                  <li key={item.id}>
                    <div className={`rounded-[14px] border-2 border-b-[3px] ${colors.card} ${colors.cardBorder}
                      flex items-center gap-3 px-3.5 py-2.5`}>
                      <button
                        type="button"
                        onClick={() => toggleItem(item)}
                        disabled={busyItem === item.id}
                        aria-pressed={item.checked}
                        aria-label={item.checked ? `Uncheck ${item.text}` : `Check ${item.text}`}
                        className="flex-shrink-0 disabled:opacity-50"
                      >
                        {item.checked ? (
                          <svg width="24" height="24" viewBox="0 0 24 24" style={{ fill: pair[0] }}>
                            <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.2 14.2l-4-4 1.4-1.4 2.6 2.6 5.6-5.6 1.4 1.4z" />
                          </svg>
                        ) : (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="1.8" className={colors.textMuted}>
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                        )}
                      </button>
                      <span className={`flex-1 min-w-0 text-[15px] font-semibold break-words
                        ${item.checked ? `line-through ${colors.textSecondary}` : ''}`}>
                        {item.text}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeItem(item)}
                        aria-label={`Delete ${item.text}`}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                          ${colors.textMuted} hover:text-dojo-red transition-colors`}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                          <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
                            strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Owners delete; everyone else leaves. */}
            <div className={`mt-8 pt-5 border-t-2 ${colors.cardBorder}`}>
              <SecondaryButton
                onClick={() => setConfirm({ list: openList, mode: owner ? 'delete' : 'leave' })}
                className="!text-dojo-red"
              >
                {owner ? 'Delete list' : 'Leave list'}
              </SecondaryButton>
            </div>
          </div>
        </div>

        {modal()}
        {confirmDialog()}
        {membersSheet()}
      </div>
    );
  }

  /* ------------------------------- grid view ------------------------------- */
  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        <ScreenHeader
          title="Lists"
          action={
            <ActionButton label="New list" onClick={openCreate}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            </ActionButton>
          }
        />

        <SummaryBand
          items={[
            { value: totals.lists, label: 'Lists' },
            { value: totals.items, label: 'Items' },
            { value: totals.shared, label: 'Shared', color: isDark ? '#FEC200' : '#C08A1E' },
          ]}
        />

        {banner}

        <div className="px-5">
          {loading ? (
            <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>Loading…</p>
          ) : lists.length === 0 ? (
            <div className="flex flex-col items-center text-center py-20">
              <div className={`w-[88px] h-[88px] rounded-[30px] border-2 ${colors.cardBorder} ${colors.card}
                flex items-center justify-center mb-[18px] text-[34px]`}>📋</div>
              <h2 className="text-xl font-black tracking-[-0.4px] mb-1.5">No lists yet</h2>
              <p className={`text-[15px] font-semibold mb-5 ${colors.textSecondary}`}>
                Create your first list to get started.
              </p>
              <button
                type="button"
                onClick={openCreate}
                className={`rounded-[14px] bg-dojo-red border-dojo-redDeep text-white ${raised} ${pressable}
                  px-5 pt-[13px] pb-[11px] text-[15px] font-extrabold`}
              >
                Create a list
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {lists.map((l) => {
                const pair = resolveListColors(l.color);
                const pct = l.itemCount > 0 ? Math.round(((l.checkedCount || 0) / l.itemCount) * 100) : 0;
                const shared = (l.members || []).length > 1;

                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setOpenId(l.id)}
                    className={`rounded-[15px] border-2 border-b-4 ${colors.card} p-[18px]
                      h-[180px] flex flex-col justify-between text-left ${pressable}`}
                    style={{ borderColor: readableOn(pair[0], card, MARK_MIN) }}
                  >
                    <span className="flex items-start justify-between">
                      <span className="text-[28px] leading-none">{resolveIcon(l.icon)}</span>
                      {shared && (
                        <span
                          className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0
                            ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-100'}`}
                          title={memberNames(l).join(', ')}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2.5" className={colors.textSecondary}>
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeLinecap="round" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" strokeLinecap="round" />
                          </svg>
                        </span>
                      )}
                    </span>

                    <span className="block min-w-0">
                      {/* No `block` here: line-clamp sets display:-webkit-box,
                          and the two fight over `display` -- whichever Tailwind
                          emits last wins, so the clamp silently stopped
                          applying and a long name pushed the member count out
                          of the card. */}
                      <span className="text-[16px] font-extrabold mb-1 line-clamp-2">{l.name}</span>
                      <span className={`block text-[14px] font-semibold ${colors.textSecondary}`}>
                        {l.itemCount || 0} item{(l.itemCount || 0) !== 1 ? 's' : ''}
                      </span>
                    </span>

                    <span className="block">
                      <span className="flex items-center gap-2">
                        <span className={`flex-1 h-2 rounded-full overflow-hidden
                          ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-200'}`}>
                          <span className="block h-full rounded-full"
                            style={{ width: `${pct}%`, backgroundColor: readableOn(pair[0], card, MARK_MIN) }} />
                        </span>
                        <span className={`text-[11px] font-extrabold tabular-nums ${colors.textSecondary}`}>
                          {pct}%
                        </span>
                      </span>
                      {/* Always rendered, so cards keep a consistent height. */}
                      <span className={`block text-[11px] font-semibold mt-1 h-[14px] ${colors.textSecondary}`}>
                        {shared ? `${l.members.length} members` : ' '}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {modal()}
      {confirmDialog()}
    </div>
  );

  /* --------------------------- shared fragments --------------------------- */

  function modal() {
    if (!creating && !editing) return null;
    return (
      <div
        className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
        onClick={closeModal}
      >
        <form
          onSubmit={saveList}
          onClick={(e) => e.stopPropagation()}
          className={`w-full sm:max-w-lg max-h-[92vh] overflow-y-auto ${colors.card}
            border-2 ${colors.cardBorder} rounded-t-[22px] sm:rounded-[22px] p-6`}
        >
          <div className="flex items-center gap-3 mb-5">
            <h2 className="text-[26px] font-black tracking-[-0.7px] flex-1">
              {editing ? 'Edit list' : 'New list'}
            </h2>
            <button
              type="button"
              onClick={closeModal}
              aria-label="Close"
              className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors.textSecondary}`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <Field label="Name" className="mb-4">
            <input
              className={input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Shopping"
              autoFocus
            />
          </Field>

          <Field label="Icon" className="mb-4">
            <div className="flex flex-wrap gap-2">
              {EMOJI_OPTIONS.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setIcon(e)}
                  aria-pressed={icon === e}
                  className={`w-10 h-10 rounded-[12px] border-2 text-[19px] flex items-center justify-center
                    ${icon === e
                      ? 'border-dojo-red bg-dojo-red/10'
                      : `${colors.cardBorder} ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'}`}`}
                >
                  {e}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Colour" className="mb-5">
            <div className="flex flex-wrap gap-2">
              {LIST_COLORS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setColor(o.colors)}
                  aria-label={`Colour ${o.id}`}
                  aria-pressed={color[0] === o.colors[0]}
                  className={`w-9 h-9 rounded-xl border-2 transition-transform
                    ${color[0] === o.colors[0] ? 'scale-110 border-current' : 'border-transparent hover:scale-105'}`}
                  style={{ background: `linear-gradient(135deg, ${o.colors[0]}, ${o.colors[1]})` }}
                />
              ))}
            </div>
          </Field>

          {error && (
            <div role="alert" className="mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
              px-4 py-3 text-[13px] font-semibold text-dojo-red">
              {error}
            </div>
          )}

          <PrimaryButton type="submit" disabled={saving} className="w-full">
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create list'}
          </PrimaryButton>
        </form>
      </div>
    );
  }

  /**
   * Who is on a list, and who may change that.
   *
   * Owner only, matching the phone. firestore.rules would allow any member to
   * edit `members`, but a member quietly removing other people from somebody
   * else's list is not a power worth inventing on the web.
   *
   * Only connections can be added: adding an arbitrary uid would write someone
   * who cannot read the list, and the list would simply not appear for them.
   */
  function membersSheet() {
    if (!membersOpen || !openList) return null;
    const l = openList;
    const current = l.members || [];
    const addable = connections.filter((c: any) => !current.includes(c.uid));

    return (
      <div
        className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
        onClick={() => setMembersOpen(false)}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={`w-full sm:max-w-md max-h-[85vh] overflow-y-auto ${colors.card}
            border-2 ${colors.cardBorder} rounded-t-[22px] sm:rounded-[22px] p-6`}
        >
          <div className="flex items-center gap-3 mb-5">
            <h2 className="text-[24px] font-black tracking-[-0.6px] flex-1">Members</h2>
            <button
              type="button"
              onClick={() => setMembersOpen(false)}
              aria-label="Close"
              className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors.textSecondary}`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <p className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2 ${colors.textMuted}`}>
            On this list
          </p>
          <ul className="space-y-2 list-none p-0 m-0 mb-6">
            {current.map((m) => {
              const isTheOwner = m === l.owner;
              const c = connections.find((x: any) => x.uid === m);
              const label = m === uid ? 'You' : (c?.displayName || c?.email || 'Someone');
              return (
                <li key={m}>
                  <div className={`rounded-[12px] border-2 ${colors.cardBorder}
                    ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'} px-3 py-2.5 flex items-center gap-2`}>
                    <span className="flex-1 min-w-0 text-[14px] font-bold truncate">
                      {label}
                      {isTheOwner && (
                        <span className={`ml-2 text-[11px] font-bold uppercase tracking-[0.4px]
                          ${colors.textSecondary}`}>owner</span>
                      )}
                    </span>
                    {!isTheOwner && (
                      <button
                        type="button"
                        onClick={() => removeMember(l, m)}
                        disabled={saving}
                        className={`text-[13px] font-extrabold flex-shrink-0 ${colors.textSecondary}
                          hover:text-dojo-red transition-colors disabled:opacity-50`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <p className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2 ${colors.textMuted}`}>
            Add a connection
          </p>
          {addable.length === 0 ? (
            <p className={`text-[13.5px] font-semibold ${colors.textSecondary}`}>
              {connections.length === 0
                ? 'Connect with someone first, then you can share this list with them.'
                : 'Everyone you are connected to is already on this list.'}
            </p>
          ) : (
            <ul className="space-y-2 list-none p-0 m-0">
              {addable.map((c: any) => (
                <li key={c.uid}>
                  <button
                    type="button"
                    onClick={() => addMember(l, c.uid)}
                    disabled={saving}
                    className={`w-full rounded-[12px] ${raised} ${colors.cardBorder} ${colors.card}
                      px-3 py-2.5 flex items-center gap-2 text-left ${pressable} disabled:opacity-50`}
                  >
                    <span className="flex-1 min-w-0 text-[14px] font-bold truncate">
                      {c.displayName || c.email}
                    </span>
                    <span className="text-[13px] font-extrabold text-dojo-red flex-shrink-0">Add</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  function confirmDialog() {
    if (!confirm) return null;
    const { list, mode } = confirm;
    const others = memberNames(list).filter((n) => n !== 'You');

    return (
      <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-5">
        <div className={`w-full max-w-sm ${colors.card} border-2 ${colors.cardBorder} rounded-[22px] p-6`}>
          <h3 className="text-xl font-black tracking-[-0.4px] mb-2">
            {mode === 'delete' ? 'Delete this list?' : 'Leave this list?'}
          </h3>
          <p className={`text-[14px] font-semibold mb-5 ${colors.textSecondary}`}>
            {mode === 'delete' ? (
              <>
                “{list.name}” and its {list.itemCount || 0} item
                {(list.itemCount || 0) !== 1 ? 's' : ''} will be removed
                {others.length > 0 ? ` for you and ${others.join(', ')}` : ''}. This can't be undone.
              </>
            ) : (
              <>
                You'll be taken off “{list.name}”. It stays for
                {others.length > 0 ? ` ${others.join(', ')}` : ' everyone else'}, and you can be added back.
              </>
            )}
          </p>
          <div className="flex gap-3">
            <SecondaryButton onClick={() => setConfirm(null)} className="flex-1">Cancel</SecondaryButton>
            <PrimaryButton
              onClick={() => (mode === 'delete' ? removeList(list) : leaveList(list))}
              disabled={saving}
              className="flex-1"
            >
              {saving ? 'Working…' : mode === 'delete' ? 'Delete' : 'Leave'}
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }
};

export default Lists;
